import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import agent from '../api/axios';
import type {
  DateRangeReport,
  MultiYearComparison,
  MonthlyBreakdown,
  Employee,
} from '../types/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Reports() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Available years
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Date range report state
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [selectedContractor, setSelectedContractor] = useState<string>('');
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [dateRangeReport, setDateRangeReport] =
    useState<DateRangeReport | null>(null);
  const [contractors, setContractors] = useState<Employee[]>([]);

  // Multi-year comparison state
  const [compareStartYear, setCompareStartYear] = useState<number>(
    new Date().getFullYear() - 2
  );
  const [compareEndYear, setCompareEndYear] = useState<number>(
    new Date().getFullYear()
  );
  const [multiYearData, setMultiYearData] =
    useState<MultiYearComparison | null>(null);

  // Monthly breakdown state
  const [monthlyYear, setMonthlyYear] = useState<number>(
    new Date().getFullYear()
  );
  const [monthlyData, setMonthlyData] = useState<MonthlyBreakdown | null>(null);
  const [monthlyViewType, setMonthlyViewType] = useState<'payments' | 'hours'>(
    'payments'
  );

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [years, payees] = await Promise.all([
          agent.Reports.getAvailableYears(),
          agent.Employees.list(),
        ]);
        setAvailableYears(years);
        setContractors(payees);
        if (years.length > 0) {
          setMonthlyYear(years[0]);
          setCompareEndYear(years[0]);
          setCompareStartYear(Math.max(years[years.length - 1], years[0] - 2));
        }
      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    };
    loadInitialData();
  }, []);

  // Fetch date range report
  const fetchDateRangeReport = async () => {
    setLoading(true);
    setError('');
    try {
      const report = await agent.Reports.getDateRangeReport({
        startDate,
        endDate,
        contractorId: selectedContractor || undefined,
        minAmount: minAmount ? parseFloat(minAmount) : undefined,
        maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
      });
      setDateRangeReport(report);
    } catch (err) {
      setError('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  // Fetch multi-year comparison
  const fetchMultiYearComparison = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await agent.Reports.getMultiYearComparison(
        compareStartYear,
        compareEndYear
      );
      setMultiYearData(data);
    } catch (err) {
      setError('Failed to fetch comparison data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch monthly breakdown
  const fetchMonthlyBreakdown = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await agent.Reports.getMonthlyBreakdown(monthlyYear);
      setMonthlyData(data);
    } catch (err) {
      setError('Failed to fetch monthly data');
    } finally {
      setLoading(false);
    }
  };

  // Export CSV
  const handleExportCsv = async () => {
    try {
      const blob = await agent.Reports.exportCsv({
        startDate,
        endDate,
        contractorId: selectedContractor || undefined,
        minAmount: minAmount ? parseFloat(minAmount) : undefined,
        maxAmount: maxAmount ? parseFloat(maxAmount) : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${startDate}_${endDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export CSV');
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Reports
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<DateRangeIcon />}
            label="Custom Date Range"
            iconPosition="start"
          />
          <Tab
            icon={<CompareArrowsIcon />}
            label="Year Comparison"
            iconPosition="start"
          />
          <Tab
            icon={<CalendarMonthIcon />}
            label="Monthly Breakdown"
            iconPosition="start"
          />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Custom Date Range Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={contractors}
                  getOptionLabel={(option) =>
                    option.companyName ||
                    `${option.firstName} ${option.lastName}`
                  }
                  value={
                    contractors.find((c) => c.id === selectedContractor) || null
                  }
                  onChange={(_, value) =>
                    setSelectedContractor(value?.id || '')
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Contractor (Optional)" />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Min Amount"
                  type="number"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Max Amount"
                  type="number"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="contained"
                  onClick={fetchDateRangeReport}
                  disabled={loading}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Run'}
                </Button>
              </Grid>
            </Grid>

            {dateRangeReport && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Total Contractors
                        </Typography>
                        <Typography variant="h4">
                          {dateRangeReport.totalContractors}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Requiring 1099
                        </Typography>
                        <Typography variant="h4">
                          {dateRangeReport.contractorsOver600}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Total Payments
                        </Typography>
                        <Typography variant="h4">
                          {formatCurrency(dateRangeReport.totalPayments)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Total Hours
                        </Typography>
                        <Typography variant="h4">
                          {dateRangeReport.totalHours.toFixed(1)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}
                >
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleExportCsv}
                  >
                    Export CSV
                  </Button>
                </Box>

                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 700 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Contractor</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">Payments</TableCell>
                        <TableCell align="right">Timesheets</TableCell>
                        <TableCell align="right">Avg Hrs/Week</TableCell>
                        <TableCell>1099</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dateRangeReport.contractors.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>{c.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={
                                c.paymentType === 'flatRate'
                                  ? 'Flat Rate'
                                  : 'Hourly'
                              }
                              size="small"
                              color={
                                c.paymentType === 'flatRate'
                                  ? 'secondary'
                                  : 'primary'
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            {c.totalHours.toFixed(1)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(c.totalPayments)}
                          </TableCell>
                          <TableCell align="right">
                            {c.timesheetCount}
                          </TableCell>
                          <TableCell align="right">
                            {c.averageHoursPerWeek.toFixed(1)}
                          </TableCell>
                          <TableCell>
                            {c.requires1099 ? (
                              <Chip
                                label="Required"
                                color="warning"
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="Under $600"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </TabPanel>

        {/* Year Comparison Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Start Year</InputLabel>
                  <Select
                    value={compareStartYear}
                    label="Start Year"
                    onChange={(e) =>
                      setCompareStartYear(Number(e.target.value))
                    }
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth>
                  <InputLabel>End Year</InputLabel>
                  <Select
                    value={compareEndYear}
                    label="End Year"
                    onChange={(e) => setCompareEndYear(Number(e.target.value))}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <Button
                  variant="contained"
                  onClick={fetchMultiYearComparison}
                  disabled={loading}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Compare'}
                </Button>
              </Grid>
            </Grid>

            {multiYearData && multiYearData.years.length > 0 && (
              <>
                <Box sx={{ height: 300, mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    Year-over-Year Payments
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={multiYearData.years}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis
                        tickFormatter={(value) =>
                          `$${(value / 1000).toFixed(0)}k`
                        }
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(Number(value))}
                        labelFormatter={(label) => `Year ${label}`}
                      />
                      <Legend />
                      <Bar
                        dataKey="totalPayments"
                        name="Total Payments"
                        fill="#1976d2"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell align="right">Total Payments</TableCell>
                        <TableCell align="right">Total Hours</TableCell>
                        <TableCell align="right">Contractors</TableCell>
                        <TableCell align="right">1099 Required</TableCell>
                        <TableCell align="right">Avg/Contractor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {multiYearData.years.map((year) => (
                        <TableRow key={year.year}>
                          <TableCell>{year.year}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(year.totalPayments)}
                          </TableCell>
                          <TableCell align="right">
                            {year.totalHours.toFixed(1)}
                          </TableCell>
                          <TableCell align="right">
                            {year.contractorCount}
                          </TableCell>
                          <TableCell align="right">
                            {year.contractorsRequiring1099}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(year.averagePaymentPerContractor)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {multiYearData.contractorTrends.length > 0 && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Top Contractor Trends
                    </Typography>
                    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 600 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Contractor</TableCell>
                            {multiYearData.years.map((y) => (
                              <TableCell key={y.year} align="right">
                                {y.year}
                              </TableCell>
                            ))}
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">YoY Change</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {multiYearData.contractorTrends.map((contractor) => (
                            <TableRow key={contractor.id}>
                              <TableCell>{contractor.name}</TableCell>
                              {multiYearData.years.map((y) => (
                                <TableCell key={y.year} align="right">
                                  {formatCurrency(
                                    contractor.paymentsByYear[y.year] || 0
                                  )}
                                </TableCell>
                              ))}
                              <TableCell align="right">
                                {formatCurrency(contractor.totalAllTime)}
                              </TableCell>
                              <TableCell align="right">
                                {contractor.yearOverYearChange !== 0 && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                    }}
                                  >
                                    {contractor.yearOverYearChange > 0 ? (
                                      <TrendingUpIcon
                                        color="success"
                                        fontSize="small"
                                      />
                                    ) : (
                                      <TrendingDownIcon
                                        color="error"
                                        fontSize="small"
                                      />
                                    )}
                                    <Typography
                                      variant="body2"
                                      color={
                                        contractor.yearOverYearChange > 0
                                          ? 'success.main'
                                          : 'error.main'
                                      }
                                    >
                                      {contractor.yearOverYearChange.toFixed(1)}
                                      %
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        {/* Monthly Breakdown Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={monthlyYear}
                    label="Year"
                    onChange={(e) => setMonthlyYear(Number(e.target.value))}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <Button
                  variant="contained"
                  onClick={fetchMonthlyBreakdown}
                  disabled={loading}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Load'}
                </Button>
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <ToggleButtonGroup
                  value={monthlyViewType}
                  exclusive
                  onChange={(_, v) => v && setMonthlyViewType(v)}
                  fullWidth
                >
                  <ToggleButton value="payments">Payments</ToggleButton>
                  <ToggleButton value="hours">Hours</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>

            {monthlyData && monthlyData.months.length > 0 && (
              <>
                <Box sx={{ height: 300, mb: 4 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyData.months}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="monthName" />
                      <YAxis
                        tickFormatter={(value) =>
                          monthlyViewType === 'payments'
                            ? `$${(value / 1000).toFixed(0)}k`
                            : `${value}`
                        }
                      />
                      <Tooltip
                        formatter={(value) =>
                          monthlyViewType === 'payments'
                            ? formatCurrency(Number(value))
                            : `${Number(value).toFixed(1)} hrs`
                        }
                      />
                      <Legend />
                      {monthlyViewType === 'payments' ? (
                        <Line
                          type="monotone"
                          dataKey="totalPayments"
                          name="Total Payments"
                          stroke="#1976d2"
                          strokeWidth={2}
                        />
                      ) : (
                        <Line
                          type="monotone"
                          dataKey="totalHours"
                          name="Total Hours"
                          stroke="#0D9E6F"
                          strokeWidth={2}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>

                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 550 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell align="right">Total Payments</TableCell>
                        <TableCell align="right">Total Hours</TableCell>
                        <TableCell align="right">Contractors Paid</TableCell>
                        <TableCell align="right">Timesheets</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthlyData.months.map((month) => (
                        <TableRow key={month.month}>
                          <TableCell>{month.monthName}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(month.totalPayments)}
                          </TableCell>
                          <TableCell align="right">
                            {month.totalHours.toFixed(1)}
                          </TableCell>
                          <TableCell align="right">
                            {month.contractorsPaid}
                          </TableCell>
                          <TableCell align="right">
                            {month.timesheetCount}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell>
                          <strong>Totals</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {formatCurrency(
                              monthlyData.months.reduce(
                                (sum, m) => sum + m.totalPayments,
                                0
                              )
                            )}
                          </strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>
                            {monthlyData.months
                              .reduce((sum, m) => sum + m.totalHours, 0)
                              .toFixed(1)}
                          </strong>
                        </TableCell>
                        <TableCell align="right">-</TableCell>
                        <TableCell align="right">
                          <strong>
                            {monthlyData.months.reduce(
                              (sum, m) => sum + m.timesheetCount,
                              0
                            )}
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
}
